import { Button } from '@retailsync/ui'
import { ImportSession, DataEntity } from '@retailsync/shared-types'
import { POS_TEMPLATES } from '../../data/templates'
import { API_ORIGIN, getAuthHeaders } from '../../config/api'
import {
  CubeIcon,
  UserGroupIcon,
  ArchiveBoxIcon,
  UserIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  BanknotesIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline'

interface EntitySelectStepProps {
  session: ImportSession
  onUpdate: (updates: Partial<ImportSession>) => void
}

interface EntityOption {
  id: DataEntity
  label: string
  description: string
  icon: React.ComponentType<{ className?: string }>
  gradient: string
  templateCount: number
}

const ENTITIES: EntityOption[] = [
  {
    id: 'products',
    label: 'Products',
    description: 'Product catalog with pricing, categories, stock levels, barcodes, expiry dates',
    icon: CubeIcon,
    gradient: 'from-blue-500 to-indigo-600',
    templateCount: POS_TEMPLATES.filter(t => t.entity === 'products').length,
  },
  {
    id: 'customers',
    label: 'Customers',
    description: 'Customer profiles with names, emails, phones, addresses, types, notes',
    icon: UserGroupIcon,
    gradient: 'from-emerald-500 to-teal-600',
    templateCount: POS_TEMPLATES.filter(t => t.entity === 'customers').length,
  },
  {
    id: 'inventory',
    label: 'Inventory (Branch)',
    description: 'Branch-level stock with quantities, reserved, available, warehouse zones',
    icon: ArchiveBoxIcon,
    gradient: 'from-amber-500 to-orange-600',
    templateCount: POS_TEMPLATES.filter(t => t.entity === 'inventory').length,
  },
  {
    id: 'staff',
    label: 'Staff',
    description: 'Staff users with roles, branches, departments, employment status',
    icon: UserIcon,
    gradient: 'from-violet-500 to-purple-600',
    templateCount: POS_TEMPLATES.filter(t => t.entity === 'staff').length,
  },
  {
    id: 'suppliers',
    label: 'Suppliers',
    description: 'Supplier profiles with contacts, tax IDs, payment terms, credit limits',
    icon: BuildingOfficeIcon,
    gradient: 'from-rose-500 to-pink-600',
    templateCount: POS_TEMPLATES.filter(t => t.entity === 'suppliers').length,
  },
  {
    id: 'transactions',
    label: 'Transactions',
    description: 'Sales history — product sales, quantities, payments, customer purchases',
    icon: BanknotesIcon,
    gradient: 'from-green-500 to-emerald-600',
    templateCount: POS_TEMPLATES.filter(t => t.entity === 'transactions').length,
  },
  {
    id: 'customer_ledger',
    label: 'Customer Ledger',
    description: 'Customer account history — credits, debits, payments, purchase records',
    icon: BanknotesIcon,
    gradient: 'from-cyan-500 to-sky-600',
    templateCount: POS_TEMPLATES.filter(t => t.entity === 'customer_ledger').length,
  },
  {
    id: 'supplier_ledger',
    label: 'Supplier Ledger',
    description: 'Supplier account history — purchases, payments, credit notes',
    icon: DocumentTextIcon,
    gradient: 'from-orange-500 to-red-600',
    templateCount: POS_TEMPLATES.filter(t => t.entity === 'supplier_ledger').length,
  },
  {
    id: 'purchase_orders',
    label: 'Purchase Orders',
    description: 'Supplier purchase orders with items, quantities, delivery dates',
    icon: DocumentTextIcon,
    gradient: 'from-yellow-500 to-amber-600',
    templateCount: POS_TEMPLATES.filter(t => t.entity === 'purchase_orders').length,
  },
  {
    id: 'recipes',
    label: 'Recipes',
    description: 'Production recipes with ingredients, quantities, costs',
    icon: CubeIcon,
    gradient: 'from-lime-500 to-green-600',
    templateCount: POS_TEMPLATES.filter(t => t.entity === 'recipes').length,
  },
  {
    id: 'production_batches',
    label: 'Production Batches',
    description: 'Batch production runs with planned/actual quantities, costs',
    icon: ArchiveBoxIcon,
    gradient: 'from-teal-500 to-cyan-600',
    templateCount: POS_TEMPLATES.filter(t => t.entity === 'production_batches').length,
  },
]

const escapeCsvValue = (value: string): string => {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

/**
 * Placeholder for the sample rows' branch cell, filled in from the tenant's real branches.
 *
 * The samples carried a literal "Main Branch", which is only a real branch for one tenant —
 * everyone else was handed a sample naming a branch they do not have.
 */
const BRANCH_PLACEHOLDER = '{{branch}}'
/** A SECOND, different branch — used by transfer-style samples where both ends are branches. */
const BRANCH2_PLACEHOLDER = '{{branch2}}'

/**
 * The tenant's branch names, fetched once per session.
 *
 * The connector imports on behalf of a whole TENANT, so unlike a POS terminal there is no single
 * "current branch". The FIRST branch is used, which is deliberately the same one the server falls
 * back to when a row leaves the branch blank — sample and behaviour agree rather than disagree.
 * An empty list means "could not determine", and the cell is then left blank.
 */
let branchNamesCache: string[] | null = null

async function loadBranchNames(): Promise<string[]> {
  if (branchNamesCache) return branchNamesCache
  let names: string[] = []
  try {
    const res = await fetch(`${API_ORIGIN}/api/v1/branches`, { headers: getAuthHeaders() })
    if (res.ok) {
      const body: any = await res.json()
      const rows = Array.isArray(body?.data) ? body.data : Array.isArray(body) ? body : []
      names = rows.map((b: any) => String(b?.name || '').trim()).filter(Boolean)
    }
  } catch {
    names = []
  }
  branchNamesCache = names
  return names
}

const CSV_TEMPLATE_DATA: Record<DataEntity, { fileName: string; headers: string[]; sampleRow: string[] }> = {
  products: {
    fileName: 'products-template.csv',
    headers: ['name', 'sku', 'description', 'barcode', 'category', 'brand', 'unit', 'department', 'price', 'cost_price', 'wholesale_price', 'bulk_min_quantity', 'bulk_price', 'sell_by', 'stock_quantity', 'min_stock_level', 'max_stock_level', 'is_active', 'show_online', 'item_type', 'requires_prescription', 'expiry_date', 'batch_number', 'manufacturer'],
    sampleRow: ['Paracetamol 500mg', 'MED-0001', 'Pain relief tablets', '8901234567890', 'Medications', 'MediCare', 'unit', 'Analgesics', '12.50', '7.20', '10.00', '1', '0', 'unit', '120', '20', '200', 'true', 'true', 'product', 'false', '2027-12-31', 'BATCH-PAR-001', 'MediCare Labs'],
  },
  customers: {
    fileName: 'customers-template.csv',
    headers: ['full_name', 'email', 'phone', 'customer_type', 'address', 'city', 'state', 'postal_code', 'country', 'date_of_birth', 'gender', 'is_active', 'notes'],
    sampleRow: ['Amina Okafor', 'amina@example.com', '+2348010001001', 'retail', '12 Palm Avenue', 'Lagos', 'Lagos', '100001', 'Nigeria', '1990-04-13', 'female', 'true', 'Loyal customer'],
  },
  inventory: {
    fileName: 'inventory-template.csv',
    headers: ['product_sku', 'branch_name', 'warehouse_name', 'quantity', 'reserved_quantity', 'min_stock_level', 'max_stock_level', 'location'],
    sampleRow: ['MED-0001', BRANCH_PLACEHOLDER, '', '120', '5', '20', '200', 'Aisle-A'],
  },
  staff: {
    fileName: 'staff-template.csv',
    headers: ['email', 'full_name', 'role', 'password', 'phone', 'branch_name', 'department', 'employee_code', 'is_active', 'hire_date'],
    sampleRow: ['manager@acmepharma.com', 'Tunde Manager', 'manager', '', '+2348011110002', BRANCH_PLACEHOLDER, 'Management', 'EMP-001', 'true', '2026-01-15'],
  },
  suppliers: {
    fileName: 'suppliers-template.csv',
    headers: ['name', 'contact_person', 'email', 'phone', 'address', 'tax_id', 'payment_terms', 'credit_limit', 'is_active', 'notes'],
    sampleRow: ['MediCare Labs', 'John Okonkwo', 'john@medicare.com', '+2348023456789', 'Industrial Estate, Lagos', 'TAX-MCL-001', 'net_30', '500000', 'true', 'Preferred supplier'],
  },
  customer_ledger: {
    fileName: 'customer-ledger-template.csv',
    headers: ['customer_email', 'transaction_type', 'amount', 'reference', 'transaction_date', 'notes'],
    sampleRow: ['amina@example.com', 'credit', '120.50', 'INV-2026-001', '2026-05-05T10:30:00Z', 'Opening balance'],
  },
  supplier_ledger: {
    fileName: 'supplier-ledger-template.csv',
    headers: ['supplier_name', 'transaction_type', 'amount', 'reference', 'transaction_date', 'notes'],
    sampleRow: ['MediCare Labs', 'debit', '250000.00', 'PO-2026-001', '2026-05-01T08:00:00Z', 'Purchase order'],
  },
  transactions: {
    fileName: 'transactions-template.csv',
    headers: ['product_sku', 'quantity', 'unit_price', 'total', 'payment_method', 'customer_email', 'timestamp'],
    sampleRow: ['MED-0001', '2', '12.50', '25.00', 'cash', 'amina@example.com', '2026-05-01T10:30:00Z'],
  },
  purchase_orders: {
    fileName: 'purchase-orders-template.csv',
    headers: ['supplier_name', 'order_number', 'status', 'order_date', 'expected_delivery_date', 'product_sku', 'quantity', 'unit_price', 'total_amount', 'notes'],
    sampleRow: ['MediCare Labs', 'PO-2026-001', 'pending', '2026-06-01', '2026-06-10', 'MED-0001', '100', '7.20', '720.00', 'Q3 restock'],
  },
  recipes: {
    fileName: 'recipes-template.csv',
    headers: ['name', 'description', 'output_product_sku', 'output_quantity', 'unit', 'labor_cost', 'overhead_cost', 'ingredients_json', 'status'],
    sampleRow: ['Amoxicillin Capsules', '100-capsule batch', 'MED-AMOX-500', '100', 'capsule', '50.00', '20.00', '[{"name":"Amoxicillin Powder","quantity":50,"unit":"g","cost":200}]', 'active'],
  },
  production_batches: {
    fileName: 'production-batches-template.csv',
    headers: ['recipe_name', 'batch_number', 'planned_quantity', 'actual_quantity', 'waste_quantity', 'status', 'start_date', 'completion_date', 'notes'],
    sampleRow: ['Amoxicillin Capsules', 'BATCH-2026-001', '100', '98', '2', 'completed', '2026-06-05', '2026-06-05', 'Minor waste during encapsulation'],
  },
  compliance_logs: {
    fileName: 'compliance-logs-template.csv',
    headers: ['event_type', 'description', 'regulatory_body', 'compliance_date', 'status', 'reference_number', 'notes'],
    sampleRow: ['inspection', 'Annual pharmacy inspection', 'NAFDAC', '2026-06-20', 'passed', 'NAFDAC-INSP-2026-001', 'No violations found'],
  },
  stock_transfers: {
    fileName: 'stock-transfers-template.csv',
    headers: ['product_sku', 'source_branch_name', 'destination_branch_name', 'quantity', 'status', 'transfer_date', 'notes'],
    sampleRow: ['MED-0001', BRANCH_PLACEHOLDER, BRANCH2_PLACEHOLDER, '25', 'pending', '2026-06-15', 'Rebalance stock'],
  },
  expenses: {
    fileName: 'expenses-template.csv',
    headers: ['category', 'description', 'amount', 'expense_date', 'payment_method', 'reference', 'notes'],
    sampleRow: ['Utilities', 'Monthly electricity bill', '45000.00', '2026-06-01', 'bank_transfer', 'EXP-2026-001', 'June billing'],
  },
  patients: {
    fileName: 'patients-template.csv',
    headers: ['full_name', 'email', 'phone', 'date_of_birth', 'gender', 'address', 'insurance_provider', 'insurance_number', 'allergies', 'notes'],
    sampleRow: ['Amina Okafor', 'amina@example.com', '+2348010001001', '1990-04-13', 'female', '12 Palm Avenue, Lagos', 'Hygeia', 'HMO-00123', 'Penicillin', 'Regular patient'],
  },
  prescriptions: {
    fileName: 'prescriptions-template.csv',
    headers: ['patient_email', 'prescriber_name', 'medication_sku', 'dosage', 'frequency', 'duration', 'issued_date', 'status', 'notes'],
    sampleRow: ['amina@example.com', 'Dr. Bello', 'MED-0001', '500mg', 'twice daily', '7 days', '2026-05-20', 'active', 'Take after meals'],
  },
  clinical_events: {
    fileName: 'clinical-events-template.csv',
    headers: ['patient_email', 'event_type', 'description', 'event_date', 'practitioner', 'outcome', 'notes'],
    sampleRow: ['amina@example.com', 'consultation', 'Routine check-up', '2026-05-18', 'Dr. Bello', 'stable', 'Blood pressure normal'],
  },
}

async function downloadCsvTemplate(entity: DataEntity): Promise<void> {
  const data = CSV_TEMPLATE_DATA[entity]
  if (!data) return
  // Only pay for the lookup on the templates that actually carry a branch cell.
  const needsBranch = data.sampleRow.some(
    cell => cell === BRANCH_PLACEHOLDER || cell === BRANCH2_PLACEHOLDER,
  )
  const names = needsBranch ? await loadBranchNames() : []
  const sourceBranch = names[0] || ''
  // For a transfer sample the destination must be a DIFFERENT branch, so take the first other
  // one; a single-branch tenant gets a blank rather than a nonsensical self-transfer.
  const destinationBranch = names.find(n => n !== sourceBranch) || ''
  const sample = data.sampleRow.map(cell => {
    if (cell === BRANCH_PLACEHOLDER) return sourceBranch
    if (cell === BRANCH2_PLACEHOLDER) return destinationBranch
    return cell
  })
  const csvLines = [
    data.headers.join(','),
    sample.map(escapeCsvValue).join(','),
  ]
  const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', data.fileName)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export function EntitySelectStep({ session, onUpdate }: EntitySelectStepProps) {
  const handleSelect = (entity: DataEntity) => {
    onUpdate({
      entity,
      selectedTemplate: undefined,
      mappings: [],
    })
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-900">What would you like to import?</h2>
        <p className="mt-1 text-sm text-gray-500">Select the type of data you're bringing into RetailsyncGo</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {ENTITIES.map((entity) => {
          const isSelected = session.entity === entity.id
          return (
            <button
              key={entity.id}
              onClick={() => handleSelect(entity.id)}
              className={`relative text-left p-5 rounded-xl border-2 transition-all ${
                isSelected
                  ? 'border-blue-500 bg-blue-50 shadow-md'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${entity.gradient} flex items-center justify-center shrink-0`}>
                  <entity.icon className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">{entity.label}</h3>
                    {entity.templateCount > 0 && (
                      <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">
                        {entity.templateCount} template{entity.templateCount !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{entity.description}</p>
                </div>
                {isSelected && (
                  <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center shrink-0">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </div>
            </button>
          )
        })}
      </div>

      {/* Download template hint */}
      <div className="flex flex-col items-center gap-3 p-4 bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-600">
          Need a template? Download a sample CSV to see the expected format.
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          {(['products', 'customers', 'inventory', 'staff', 'suppliers', 'transactions', 'purchase_orders', 'recipes', 'production_batches'] as DataEntity[]).map(entity => (
            <Button key={entity} size="sm" variant="outline" onClick={() => { void downloadCsvTemplate(entity) }}>
              <ArrowDownTrayIcon className="h-3.5 w-3.5 mr-1" />
              {entity.replace(/_/g, ' ')}
            </Button>
          ))}
        </div>
      </div>
    </div>
  )
}
